import type { ReactNode } from 'react';
import type { PublicModuleCatalogEntryResponse } from '@tabita/contracts';
import { formatMoney } from '@tabita/i18n';
import { useI18n } from '@/i18n/i18n-provider';
import { Button } from '@/components/ui';
import { withDependencies } from './module-dependencies';

/** Display order — the always-on baseline first, then shop categories roughly cheapest-to-broadest. */
const CATEGORY_ORDER = [
  'PLATFORM',
  'FOUNDATION',
  'ENGAGEMENT',
  'MINISTRY',
  'FINANCE',
  'OPERATIONS',
  'DIGITAL',
  'INTELLIGENCE',
  'ENTERPRISE',
] as const;

function priceLabel(
  entry: PublicModuleCatalogEntryResponse,
  locale: string,
  t: ReturnType<typeof useI18n>['t'],
): string {
  if (entry.isBaseline) return t('platform.modules.included');
  if (entry.isFree) return t('platform.modules.free');
  return t('platform.modules.priceMonthly', {
    values: { amount: formatMoney(entry.priceMonthlyCents ?? 0, 'USD', locale) },
  });
}

/**
 * The full-catalog grid, grouped by category — shared by the registration
 * wizard's module step and `/billing`'s "manage modules" screen, the two
 * places a church picks its bundle. Shows the whole system, not just the
 * add-ons: baseline modules (`isBaseline` — every `PLATFORM` module, plus
 * `PEOPLE`) render as a permanently-included card, never toggled and never
 * part of `selectedModuleKeys`, so a prospective church sees the complete
 * picture before deciding what to add. Selecting a shop module auto-selects
 * its unmet dependencies; a module something else still needs can't be
 * dropped on its own (see `module-dependencies.ts`).
 */
export function ModulePicker({
  catalog,
  selectedModuleKeys,
  onChange,
}: {
  readonly catalog: readonly PublicModuleCatalogEntryResponse[];
  readonly selectedModuleKeys: readonly string[];
  readonly onChange: (moduleKeys: string[]) => void;
}): ReactNode {
  const { t, locale } = useI18n();
  const selected = new Set(selectedModuleKeys);
  const lockedKeys = new Set(
    catalog.flatMap((entry) => (selected.has(entry.key) ? entry.dependencies : [])),
  );

  function toggle(entry: PublicModuleCatalogEntryResponse): void {
    if (entry.isBaseline) return;
    if (selected.has(entry.key)) {
      if (lockedKeys.has(entry.key)) return;
      onChange(selectedModuleKeys.filter((key) => key !== entry.key));
    } else {
      const shopKeys = new Set(
        catalog.filter((candidate) => !candidate.isBaseline).map((candidate) => candidate.key),
      );
      const toAdd = withDependencies(entry.key, catalog).filter((key) => shopKeys.has(key));
      onChange([...new Set([...selectedModuleKeys, ...toAdd])]);
    }
  }

  const groups = CATEGORY_ORDER.map((category) => ({
    category,
    entries: catalog.filter((entry) => entry.category === category),
  })).filter((group) => group.entries.length > 0);

  return (
    <>
      {groups.map((group) => (
        <section key={group.category} className="plan-category-group">
          <h3 className="plan-category-label">
            {t(`platform.modules.category.${group.category}`)}
          </h3>
          <div className="plan-grid">
            {group.entries.map((entry) => {
              const isSelected = entry.isBaseline || selected.has(entry.key);
              const isLocked = entry.isBaseline || (isSelected && lockedKeys.has(entry.key));
              return (
                <Button
                  key={entry.key}
                  type="button"
                  className="plan-card"
                  data-selected={isSelected}
                  disabled={isLocked}
                  onClick={() => toggle(entry)}
                >
                  <span className="plan-card-name">{entry.title}</span>
                  <span className="plan-card-tagline">{entry.description}</span>
                  <span className="plan-card-trial">{priceLabel(entry, locale, t)}</span>
                  {isLocked && !entry.isBaseline && (
                    <span className="plan-card-tagline">{t('registration.modules.required')}</span>
                  )}
                </Button>
              );
            })}
          </div>
        </section>
      ))}
    </>
  );
}

/** The running total for a selected set — used by both the registration footer and the billing screen. */
export function totalMonthlyCents(
  catalog: readonly PublicModuleCatalogEntryResponse[],
  selectedModuleKeys: readonly string[],
): number {
  const selected = new Set(selectedModuleKeys);
  return catalog
    .filter((entry) => !entry.isBaseline && selected.has(entry.key))
    .reduce((sum, entry) => sum + (entry.isFree ? 0 : (entry.priceMonthlyCents ?? 0)), 0);
}
